import React from "react";

// --------------------------------------------------------------- <Pagination>
// One shared, industrial-style pager for every long table on the app.
// Renders: rows-per-page selector (10/20/30/40/50), page-number pills, prev/next.
//
// Contract (controlled): the parent owns page + pageSize state and the total
// item count. This hook returns nothing — parent decides how to slice its data.
//
//   const [page, setPage] = useState(1);
//   const [pageSize, setPageSize] = useState(20);
//   const paged = items.slice((page-1)*pageSize, page*pageSize);
//   ...
//   <Pagination page={page} pageSize={pageSize} total={items.length}
//               onPageChange={setPage} onPageSizeChange={setPageSize} />
//
// Every interactive element carries a stable data-testid so the testing agent
// can drive it without brittle text lookups.

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  testId = "pagination",
  className = "",
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIdx = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIdx = Math.min(currentPage * pageSize, total);

  // A ⇨ … ⇦ B window: first, last, current ± 1. Prevents 100-page overflows.
  const pageNumbers = React.useMemo(() => {
    const set = new Set([1, totalPages,
                         currentPage - 1, currentPage, currentPage + 1]);
    const out = [...set].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    // Insert gap markers (represented as -1) where numbers jump by >1.
    const withGaps = [];
    for (let i = 0; i < out.length; i++) {
      if (i > 0 && out[i] - out[i - 1] > 1) withGaps.push(-1);
      withGaps.push(out[i]);
    }
    return withGaps;
  }, [currentPage, totalPages]);

  const setSize = (n) => {
    const first = (currentPage - 1) * pageSize + 1;
    // Keep the top of the viewport on the same record when growing/shrinking.
    const newPage = Math.max(1, Math.ceil(first / n));
    onPageSizeChange(n);
    onPageChange(newPage);
  };

  // Hide entirely when there's nothing to page through.
  if (total === 0) return null;

  return (
    <div
      data-testid={testId}
      className={`flex flex-wrap items-center justify-between gap-3 mt-3 px-3 py-2 border-t border-zinc-200 bg-white text-xs font-mono ${className}`}
    >
      <div className="flex items-center gap-2 text-zinc-600">
        <span className="uppercase tracking-widest text-[10px] text-zinc-500">Rows</span>
        <select
          value={pageSize}
          onChange={e => setSize(parseInt(e.target.value, 10))}
          className="border border-zinc-300 bg-white px-1.5 py-0.5 text-xs font-mono focus:outline-none focus:border-orange-600"
          data-testid={`${testId}-size`}
        >
          {PAGE_SIZE_OPTIONS.map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span className="text-zinc-500">
          {startIdx}–{endIdx} of {total}
        </span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-testid={`${testId}-prev`}
          className="px-2 py-1 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          ← Prev
        </button>
        {pageNumbers.map((n, idx) => (
          n === -1 ? (
            <span key={`gap-${idx}`} className="px-1.5 text-zinc-400" data-testid={`${testId}-gap`}>…</span>
          ) : (
            <button
              type="button"
              key={n}
              onClick={() => onPageChange(n)}
              data-testid={`${testId}-page-${n}`}
              className={
                n === currentPage
                  ? "px-2.5 py-1 bg-orange-600 text-white font-bold"
                  : "px-2.5 py-1 border border-zinc-300 hover:bg-zinc-100"
              }
            >
              {n}
            </button>
          )
        ))}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          data-testid={`${testId}-next`}
          className="px-2 py-1 border border-zinc-300 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// Reusable state hook so the parent doesn't have to re-write boilerplate.
export function usePagination(totalOrDefaultSize = 20) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(
    typeof totalOrDefaultSize === "number" ? totalOrDefaultSize : 20
  );
  // Slice helper — pass the source array, get the current page slice back.
  const slice = React.useCallback((arr) => {
    const from = (page - 1) * pageSize;
    return arr.slice(from, from + pageSize);
  }, [page, pageSize]);
  return { page, setPage, pageSize, setPageSize, slice };
}
